import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import socketService from '../services/socketService';
import Layout from '../components/Layout';
import ResilientImage from '../components/ResilientImage';
import moment from 'moment';

const DeviceTransactions = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const deviceName = searchParams.get('deviceName');
    const simSlot = searchParams.get('simSlot'); 
    
    const [transactions, setTransactions] = useState([]);
    const [activeTab, setActiveTab] = useState('All');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    
    // Balance States
    const [yesterdayBalance, setYesterdayBalance] = useState(0);
    const [yesterdayBalanceBkash, setYesterdayBalanceBkash] = useState(0); 
    const [yesterdayBalanceNagad, setYesterdayBalanceNagad] = useState(0); 
    
    // Yesterday States
    const [yesterdayCashIn, setYesterdayCashIn] = useState(0);
    const [yesterdayCashOut, setYesterdayCashOut] = useState(0);
    const [yesterdayCashInCount, setYesterdayCashInCount] = useState(0); 
    const [yesterdayCashOutCount, setYesterdayCashOutCount] = useState(0); 
    
    // Missing States - Yesterday
    const [yesterdayMissingAppCount, setYesterdayMissingAppCount] = useState(0); 
    const [yesterdayMissingAppAmount, setYesterdayMissingAppAmount] = useState(0); 
    const [yesterdayMissingSmsCount, setYesterdayMissingSmsCount] = useState(0); 
    const [yesterdayMissingSmsAmount, setYesterdayMissingSmsAmount] = useState(0); 

    // Today States
    const [todayCashIn, setTodayCashIn] = useState(0);
    const [todayCashOut, setTodayCashOut] = useState(0);
    const [todayCashInCount, setTodayCashInCount] = useState(0); 
    const [todayCashOutCount, setTodayCashOutCount] = useState(0); 

    // Missing States - Today
    const [todayMissingAppCount, setTodayMissingAppCount] = useState(0); 
    const [todayMissingAppAmount, setTodayMissingAppAmount] = useState(0); 
    const [todayMissingSmsCount, setTodayMissingSmsCount] = useState(0); 
    const [todayMissingSmsAmount, setTodayMissingSmsAmount] = useState(0); 
    
    const [simNumber, setSimNumber] = useState('Loading...');
    const [loading, setLoading] = useState(true);

    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const limit = 100; 

    useEffect(() => {
        if (!deviceName || !simSlot) return;

        const socket = socketService.getSocket();
        if (!socket.connected) socketService.connect();

        setLoading(true);
        socketService.emit('get_transaction_page_data', { 
            deviceName, simSlot, page, limit, filterType: activeTab, startDate, endDate 
        });

        const handleResponse = (data) => {
            setTransactions(data.transactions);
            
            // Calculate Yesterday's Total safely in Frontend
            const yBkash = data.yesterdayBalanceBkash || 0;
            const yNagad = data.yesterdayBalanceNagad || 0;
            setYesterdayBalanceBkash(yBkash); 
            setYesterdayBalanceNagad(yNagad); 
            setYesterdayBalance(yBkash + yNagad); 
            
            setSimNumber(data.simNumber);
            
            setYesterdayCashIn(data.yesterdayCashIn || 0);
            setYesterdayCashOut(data.yesterdayCashOut || 0);
            setYesterdayCashInCount(data.yesterdayCashInCount || 0);
            setYesterdayCashOutCount(data.yesterdayCashOutCount || 0);

            // Set Yesterday Missing Amounts
            setYesterdayMissingAppCount(data.yesterdayMissingAppCount || 0);
            setYesterdayMissingAppAmount(data.yesterdayMissingAppAmount || 0);
            setYesterdayMissingSmsCount(data.yesterdayMissingSmsCount || 0);
            setYesterdayMissingSmsAmount(data.yesterdayMissingSmsAmount || 0);

            setTodayCashIn(data.todayCashIn || 0);
            setTodayCashOut(data.todayCashOut || 0);
            setTodayCashInCount(data.todayCashInCount || 0);
            setTodayCashOutCount(data.todayCashOutCount || 0);

            // Set Today Missing Amounts
            setTodayMissingAppCount(data.todayMissingAppCount || 0);
            setTodayMissingAppAmount(data.todayMissingAppAmount || 0);
            setTodayMissingSmsCount(data.todayMissingSmsCount || 0);
            setTodayMissingSmsAmount(data.todayMissingSmsAmount || 0);
            
            if(data.pagination) setTotalPages(data.pagination.totalPages);
            setLoading(false);
        };

        socket.on('transaction_page_response', handleResponse);

        return () => {
            socket.off('transaction_page_response', handleResponse);
        };
    }, [deviceName, simSlot, page, activeTab, startDate, endDate]); 

    const handlePageChange = (newPage) => { if (newPage >= 1 && newPage <= totalPages) setPage(newPage); };
    const handleTabChange = (tab) => { setActiveTab(tab); setPage(1); };

    const applyDateFilter = () => {
        setPage(1); 
        if (page === 1) {
            setLoading(true);
            socketService.emit('get_transaction_page_data', { deviceName, simSlot, page: 1, limit, filterType: activeTab, startDate, endDate });
        }
    };

    const clearDateFilter = () => {
        setStartDate(''); setEndDate(''); setPage(1);
        if (page === 1) {
            setLoading(true);
            socketService.emit('get_transaction_page_data', { deviceName, simSlot, page: 1, limit, filterType: activeTab, startDate: '', endDate: '' });
        }
    };

    let latestBalances = { bkash: undefined, nagad: undefined };
    if (transactions.length > 0) {
        transactions.forEach(trx => {
            const sender = (trx.senderID || 'Unknown').toLowerCase();
            if (sender.includes('bkash') && latestBalances.bkash === undefined) {
                latestBalances.bkash = trx.newBalance;
            } else if (sender.includes('nagad') && latestBalances.nagad === undefined) {
                latestBalances.nagad = trx.newBalance;
            }
        });
    }

    const finalBkashBal = latestBalances.bkash !== undefined ? latestBalances.bkash : yesterdayBalanceBkash;
    const finalNagadBal = latestBalances.nagad !== undefined ? latestBalances.nagad : yesterdayBalanceNagad;
    const currentBalance = finalBkashBal + finalNagadBal;

    const getServiceLogo = (sender) => {
        if (!sender) return '/logo.png';
        const sLower = sender.toLowerCase();
        if (sLower.includes('bkash')) return '/bkash.jpg';
        if (sLower.includes('nagad')) return '/nagad.png';
        if (sLower.includes('rocket')) return '/rocket.png';
        if (sLower.includes('whatsapp')) return '/whatsapp.png';
        return '/logo.png';
    };

    const getCommission = (msg) => {
        if (!msg) return 0;
        const match = msg.match(/(?:Comm|Fee|Commission|Charge)[:\s]+Tk\s*([\d,.]+)/i);
        return match ? parseFloat(match[1].replace(/,/g, '')) : 0;
    };

    const processedTransactions = transactions.map((trx, index, arr) => {
        if (activeTab === 'Mismatch' || activeTab === 'Missing SMS' || activeTab === 'Missing App') {
            return { ...trx, isMismatch: activeTab === 'Mismatch' };
        }

        const TOLERANCE = 2.0; 
        let isMismatch = false;

        if (activeTab === 'All') {
            const currentSender = (trx.senderID || '').toLowerCase();
            const isBkash = currentSender.includes('bkash');
            const isNagad = currentSender.includes('nagad');

            const sameSenderLogs = arr.slice(index + 1).filter(t => {
                const nextSender = (t.senderID || '').toLowerCase();
                if (isBkash) return nextSender.includes('bkash');
                if (isNagad) return nextSender.includes('nagad');
                return currentSender === nextSender;
            });

            let nextTrx = sameSenderLogs.length > 0 ? sameSenderLogs[0] : null;
            let nextNextTrx = sameSenderLogs.length > 1 ? sameSenderLogs[1] : null;

            if (nextTrx) {
                const oldBal = trx.rawOldBal !== undefined ? trx.rawOldBal : (parseFloat(trx.oldBalance) || 0);
                const diff = Math.abs(oldBal - nextTrx.newBalance);
                const commission = getCommission(trx.message);

                if (diff > TOLERANCE) {
                    if (Math.abs(diff - commission) > TOLERANCE) {
                        const amount = trx.amount || 0;
                        const maxImpliedCommission = amount * 0.02; 
                        if (diff > maxImpliedCommission) {
                            isMismatch = true;
                        }
                    }
                }

                if (isMismatch && nextNextTrx) {
                    const diffSkip = Math.abs(oldBal - nextNextTrx.newBalance);
                    if (diffSkip <= TOLERANCE || (trx.amount && diffSkip < trx.amount * 0.02)) isMismatch = false; 
                }
            }
        }
        return { ...trx, isMismatch };
    });

    const tabs = ['All', 'Mismatch', 'Missing SMS', 'Missing App', 'Cash In', 'Cash Out', 'B2B Transfer', 'Received Money', 'Send Money', 'Payment'];

    const styles = `
        .stat-card { background: white; border-radius: 12px; padding: 20px; box-shadow: 0 2px 10px rgba(0,0,0,0.05); border: 1px solid #e2e8f0; height: 100%; display: flex; flex-direction: column; justify-content: space-between;}
        .stat-title { font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; margin-bottom: 5px; letter-spacing: 0.5px; }
        .stat-value { font-size: 20px; font-weight: 800; color: #0f172a; }
        
        .filter-tabs { display: flex; gap: 8px; overflow-x: auto; padding-bottom: 5px; margin: 0; }
        .filter-tab { padding: 6px 16px; border-radius: 20px; font-size: 12px; font-weight: 700; cursor: pointer; border: 1px solid #cbd5e1; background: white; color: #475569; transition: all 0.2s; white-space: nowrap; }
        .filter-tab:hover { background: #f1f5f9; border-color: #94a3b8; }
        .filter-tab.active { background: #0f172a; color: white; border-color: #0f172a; box-shadow: 0 2px 6px rgba(15, 23, 42, 0.2); }
        .filter-tab.active-mismatch, .filter-tab.active-missing { background: #dc2626 !important; color: white !important; border-color: #dc2626 !important; box-shadow: 0 2px 6px rgba(220, 38, 38, 0.3) !important; }

        .trx-table { width: 100%; border-collapse: collapse; background: white; border-radius: 12px; overflow: hidden; box-shadow: 0 2px 10px rgba(0,0,0,0.05); }
        .trx-table thead { background: #f8fafc; border-bottom: 2px solid #e2e8f0; }
        .trx-table th { padding: 15px; font-size: 11px; font-weight: 800; color: #475569; text-transform: uppercase; text-align: left; }
        .trx-table td { padding: 12px 15px; border-bottom: 1px solid #f1f5f9; font-size: 13px; color: #334155; vertical-align: middle; }
        .trx-row:hover { background-color: #f1f5f9; }
        
        .trx-row-error { background-color: #fee2e2 !important; border-left: 4px solid #dc2626 !important; }
        .trx-row-error td { color: #b91c1c !important; }
        
        .badge-type { padding: 4px 8px; border-radius: 6px; font-size: 10px; font-weight: 700; text-transform: uppercase; }
        .type-pos { background: #dcfce7; color: #166534; }
        .type-neg { background: #fee2e2; color: #991b1b; }
        .amount-pos { color: #16a34a; font-weight: 700; }
        .amount-neg { color: #dc2626; font-weight: 700; }
        .sender-info { display: flex; align-items: center; gap: 10px; }
        .sender-logo { width: 35px; height: 35px; border-radius: 50%; object-fit: cover; border: 1px solid #e2e8f0; }
        .sender-text { display: flex; flex-direction: column; }
        .sender-name { font-weight: 700; font-size: 12px; color: #1e293b; text-transform: capitalize;}
        .msg-full { white-space: normal; word-break: break-word; font-size: 12px; color: #475569; line-height: 1.5; min-width: 250px; }
        .pagination-container { display: flex; justify-content: center; align-items: center; gap: 8px; margin-top: 20px; flex-wrap: wrap; }
        .page-btn { border: 1px solid #cbd5e1; background: white; padding: 6px 12px; border-radius: 6px; font-weight: 600; font-size: 14px; color: #334155; transition: 0.2s; min-width: 35px; }
        .page-btn:hover:not(:disabled) { background: #f1f5f9; border-color: #94a3b8; }
        .page-btn.active-page { background-color: #0f172a; color: white; border-color: #0f172a; }
        .section-label { font-size: 12px; font-weight: 700; color: #94a3b8; margin-bottom: 10px; display: block; text-transform: uppercase; }
        
        .badge-seen { background: #e0f2fe; color: #0369a1; padding: 3px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; text-transform: uppercase; display: inline-block; }
        .badge-app { background: #6366f1; color: white; padding: 3px 6px; border-radius: 4px; font-size: 10px; font-weight: 700; margin-left: 6px; box-shadow: 0 1px 3px rgba(99,102,241,0.3); }
        .badge-missing { background: #dc2626; color: white; padding: 3px 6px; border-radius: 4px; font-size: 9px; font-weight: 800; margin-left: 6px; }
        .badge-warning-custom { background: #f59e0b; color: white; padding: 3px 6px; border-radius: 4px; font-size: 9px; font-weight: 800; margin-left: 6px; }
    `;

    return (
        <Layout>
            <style>{styles}</style>
            <div className="container-fluid py-4 px-4 bg-light min-vh-100">
                
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <button className="btn btn-sm btn-outline-secondary mb-2" onClick={() => navigate(-1)}>← Back</button>
                        <h4 className="fw-bold m-0 text-dark">Transaction History</h4>
                        <p className="text-muted small m-0">Device: <strong>{deviceName}</strong> | SIM: <strong>{simNumber}</strong></p>
                    </div>
                    <div className="text-end">
                        <span className="badge bg-primary">LIVE DATA</span>
                    </div>
                </div>

                <span className="section-label">Account Balance</span>
                <div className="row g-3 mb-4">
                    <div className="col-md-6">
                        <div className="stat-card">
                            <div className="stat-title">Yesterday's Closing (Total)</div>
                            <div className="stat-value text-muted">৳ {yesterdayBalance?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                            <div className="d-flex gap-2 mt-2">
                                <span className="badge bg-light text-dark border p-1" style={{fontSize: '11px'}}>
                                    <img src="/bkash.jpg" width="14" height="14" className="me-1 rounded-circle" style={{objectFit: 'cover'}} alt="bkash" />
                                    ৳{yesterdayBalanceBkash?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                                <span className="badge bg-light text-dark border p-1" style={{fontSize: '11px'}}>
                                    <img src="/nagad.png" width="14" height="14" className="me-1 rounded-circle" style={{objectFit: 'cover'}} alt="nagad" />
                                    ৳{yesterdayBalanceNagad?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="stat-card border-primary">
                            <div className="stat-title text-primary">Current Balance (Total)</div>
                            <div className="stat-value text-primary">৳ {currentBalance?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                            <div className="d-flex justify-content-between align-items-center mt-2">
                                <div className="d-flex gap-2">
                                    <span className="badge bg-light text-dark border border-primary p-1" style={{fontSize: '11px'}}>
                                        <img src="/bkash.jpg" width="14" height="14" className="me-1 rounded-circle" style={{objectFit: 'cover'}} alt="bkash" />
                                        ৳{finalBkashBal?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                    <span className="badge bg-light text-dark border border-primary p-1" style={{fontSize: '11px'}}>
                                        <img src="/nagad.png" width="14" height="14" className="me-1 rounded-circle" style={{objectFit: 'cover'}} alt="nagad" />
                                        ৳{finalNagadBal?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                </div>
                                <small className="text-success fw-bold" style={{fontSize: '10px'}}>● Updated Just Now</small>
                            </div>
                        </div>
                    </div>
                </div>

                <span className="section-label text-primary">Today's Activity <span className="badge bg-primary ms-1">Today</span></span>
                <div className="row g-3 mb-4">
                    <div className="col-lg-3 col-md-6">
                        <div className="stat-card" style={{borderLeft: '4px solid #16a34a', background: '#f0fdf4'}}>
                            <div className="stat-title text-success">Total Cash In</div>
                            <div className="stat-value text-success">৳ {todayCashIn?.toLocaleString()}</div>
                            <div className="text-muted fw-bold mt-2" style={{fontSize: '13px'}}>
                                Received • <span className="text-success">{todayCashInCount} SMS</span>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                        <div className="stat-card" style={{borderLeft: '4px solid #dc2626', background: '#fef2f2'}}>
                            <div className="stat-title text-danger">Total Cash Out</div>
                            <div className="stat-value text-danger">৳ {todayCashOut?.toLocaleString()}</div>
                            <div className="text-muted fw-bold mt-2" style={{fontSize: '13px'}}>
                                Sent • <span className="text-danger">{todayCashOutCount} SMS</span>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                        <div className="stat-card" style={{borderLeft: '4px solid #f59e0b', background: '#fffbeb'}}>
                            <div className="stat-title text-warning" style={{color: '#d97706'}}>Missing App Logs</div>
                            <div className="stat-value" style={{color: '#b45309'}}>৳ {todayMissingAppAmount?.toLocaleString()}</div>
                            <div className="text-muted fw-bold mt-2" style={{fontSize: '12px'}}>
                                Missing: <span style={{color: '#b45309', fontSize: '14px'}}>{todayMissingAppCount} Trx</span>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                        <div className="stat-card" style={{borderLeft: '4px solid #dc2626', background: '#fef2f2'}}>
                            <div className="stat-title text-danger">Missing SMS</div>
                            <div className="stat-value text-danger">৳ {todayMissingSmsAmount?.toLocaleString()}</div>
                            <div className="text-muted fw-bold mt-2" style={{fontSize: '12px'}}>
                                Missing: <span className="text-danger" style={{fontSize: '14px'}}>{todayMissingSmsCount} Trx</span>
                            </div>
                        </div>
                    </div>
                </div>

                <span className="section-label">Yesterday's Activity <span className="badge bg-secondary ms-1">Yesterday</span></span>
                <div className="row g-3 mb-4">
                    <div className="col-lg-3 col-md-6">
                        <div className="stat-card" style={{borderLeft: '4px solid #cbd5e1'}}>
                            <div className="stat-title text-muted">Total Cash In</div>
                            <div className="stat-value text-dark">৳ {yesterdayCashIn?.toLocaleString()}</div>
                            <div className="text-muted fw-bold mt-2" style={{fontSize: '13px'}}>
                                Total: <span className="text-dark">{yesterdayCashInCount} SMS</span>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                        <div className="stat-card" style={{borderLeft: '4px solid #cbd5e1'}}>
                            <div className="stat-title text-muted">Total Cash Out</div>
                            <div className="stat-value text-dark">৳ {yesterdayCashOut?.toLocaleString()}</div>
                            <div className="text-muted fw-bold mt-2" style={{fontSize: '13px'}}>
                                Total: <span className="text-dark">{yesterdayCashOutCount} SMS</span>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                        <div className="stat-card" style={{borderLeft: '4px solid #cbd5e1'}}>
                            <div className="stat-title text-muted">Missing App Logs</div>
                            <div className="stat-value text-dark">৳ {yesterdayMissingAppAmount?.toLocaleString()}</div>
                            <div className="text-muted fw-bold mt-2" style={{fontSize: '12px'}}>
                                Missing: <span className="text-dark" style={{fontSize: '14px'}}>{yesterdayMissingAppCount} Trx</span>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                        <div className="stat-card" style={{borderLeft: '4px solid #cbd5e1'}}>
                            <div className="stat-title text-muted">Missing SMS</div>
                            <div className="stat-value text-dark">৳ {yesterdayMissingSmsAmount?.toLocaleString()}</div>
                            <div className="text-muted fw-bold mt-2" style={{fontSize: '12px'}}>
                                Missing: <span className="text-dark" style={{fontSize: '14px'}}>{yesterdayMissingSmsCount} Trx</span>
                            </div>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status"></div>
                        <p className="mt-2 text-muted fw-bold">Loading Transactions...</p>
                    </div>
                ) : (
                    <>
                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                            <div className="filter-tabs">
                                {tabs.map(tab => (
                                    <button 
                                        key={tab} 
                                        className={`filter-tab ${activeTab === tab ? (['Mismatch', 'Missing SMS', 'Missing App'].includes(tab) ? 'active-missing' : 'active') : ''}`}
                                        onClick={() => handleTabChange(tab)}
                                    >
                                        {['Mismatch', 'Missing SMS', 'Missing App'].includes(tab) ? `⚠ ${tab}` : tab}
                                    </button>
                                ))}
                            </div>

                            <div className="d-flex align-items-center gap-2 bg-white p-1 rounded border shadow-sm">
                                <input type="date" className="form-control form-control-sm border-0" style={{maxWidth: '130px'}} value={startDate} onChange={e => setStartDate(e.target.value)} />
                                <span className="text-muted fw-bold small">TO</span>
                                <input type="date" className="form-control form-control-sm border-0" style={{maxWidth: '130px'}} value={endDate} onChange={e => setEndDate(e.target.value)} />
                                <button className="btn btn-sm btn-dark px-3 fw-bold rounded" onClick={applyDateFilter}>Apply</button>
                                {(startDate || endDate) && (<button className="btn btn-sm btn-outline-danger px-2 fw-bold rounded" onClick={clearDateFilter}>✕</button>)}
                            </div>
                        </div>

                        <div className="table-responsive">
                            <table className="trx-table">
                                <thead>
                                    <tr>
                                        <th>Date & Time</th>
                                        <th>Service & Type</th>
                                        <th>Trx ID</th>
                                        <th>Seen By</th>
                                        <th className="text-end">Amount</th>
                                        <th className="text-end">Old Balance</th>
                                        <th className="text-end">New Balance</th>
                                        <th style={{width: '35%'}}>Full Message</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {processedTransactions.length === 0 ? (
                                        <tr><td colSpan="8" className="text-center py-4 text-muted">No transactions found.</td></tr>
                                    ) : processedTransactions.map((trx) => {
                                        const isIncoming = ['Cash Out', 'Received Money', 'Remittance'].includes(trx.type);

                                        return (
                                            <tr key={trx._id} className={`trx-row ${trx.isMismatch || trx.isSmsMissing || trx.isAppMissing ? 'trx-row-error' : ''}`}>
                                               <td>
                                                  <td>
    <div className="fw-bold">{moment.utc(trx.date).format('DD MMM YYYY')}</div>
    <div className="text-muted small">{moment.utc(trx.date).format('hh:mm A')}</div>
</td>
                                                </td>
                                                <td>
                                                    <div className="sender-info">
                                                        <ResilientImage src={getServiceLogo(trx.senderID)} alt={trx.senderID} className="sender-logo" />
                                                        <div className="sender-text">
                                                            <span className="sender-name">{trx.senderID || 'Unknown'}</span>
                                                            <span className={`badge-type ${isIncoming ? 'type-pos' : 'type-neg'}`} style={{width: 'fit-content'}}>{trx.type}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <span className="fw-bold text-dark font-monospace">{trx.trxID}</span>
                                                        {trx.isAppConfirmed && !trx.isSmsMissing && (
                                                            <span title={`Verified by App: ${trx.appSeenBy || 'Unknown'}`}><small className="badge-app">✔ APP</small></span>
                                                        )}
                                                        {trx.isSmsMissing && (
                                                            <span title="SMS is missing for this App transaction"><small className="badge-missing">SMS MISSING</small><small className="badge-app">✔ APP</small></span>
                                                        )}
                                                        {trx.isAppMissing && (
                                                            <span title="App Log is missing for this SMS"><small className="badge-warning-custom">APP MISSING</small></span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>{trx.seenBy ? <span className="badge-seen">{trx.seenBy}</span> : <span className="text-muted small">--</span>}</td>
                                                <td className="text-end"><span className={isIncoming ? 'amount-pos' : 'amount-neg'}>{isIncoming ? '+' : '-'} ৳{trx.amount?.toLocaleString()}</span></td>
                                                <td className="text-end text-muted">{(trx.isMismatch || trx.isSmsMissing || trx.isAppMissing) && <span className="text-danger fw-bold me-1">⚠</span>}৳{trx.oldBalance?.toLocaleString()}</td>
                                                <td className="text-end fw-bold text-dark">৳{trx.newBalance?.toLocaleString()}</td>
                                                <td><div className="msg-full">{trx.message}</div></td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {processedTransactions.length > 0 && totalPages > 1 && (
                            <div className="pagination-container">
                                <button className="page-btn" disabled={page === 1} onClick={() => handlePageChange(page - 1)}>&laquo; Prev</button>
                                {(() => {
                                    const maxVisibleButtons = 8;
                                    let startPage = Math.max(1, page - Math.floor(maxVisibleButtons / 2));
                                    let endPage = Math.min(totalPages, startPage + maxVisibleButtons - 1);
                                    if (endPage - startPage + 1 < maxVisibleButtons) startPage = Math.max(1, endPage - maxVisibleButtons + 1);
                                    const pageNumbers = [];
                                    for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
                                    return pageNumbers.map((p) => (
                                        <button key={p} className={`page-btn ${page === p ? 'active-page' : ''}`} onClick={() => handlePageChange(p)}>{p}</button>
                                    ));
                                })()}
                                <button className="page-btn" disabled={page === totalPages} onClick={() => handlePageChange(page + 1)}>Next &raquo;</button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Layout>
    );
};

export default DeviceTransactions;
